import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';

export type PricingRecommendation = {
    pricingIds: string[];
    courtName: string;
    sport: string;
    // -1 is the wildcard: a row that prices every game capacity. A
    // recommendation never mixes capacities or durations, so two rows for the
    // same court/sport/window are two distinct price points and the table must
    // label them or they read as duplicates.
    capacity: number;
    slotDurationInMs: number;
    dayOfWeek: number;
    timeWindow: string;
    currentPrice: number;
    priceRange?: { min: number; max: number };
    recommendedPrice: number;
    changePercent: number;
    utilizationPercent: number;
    reason: string;
    confidence: string;
    currency: string;
};

export type PricingInsightsSummary = {
    totalCourts: number;
    totalPricings: number;
    recommendationCount: number;
    increaseCount: number;
    decreaseCount: number;
    periodStartDate: string;
    periodEndDate: string;
};

export type PricingInsightsResult = {
    summary: PricingInsightsSummary;
    recommendations: PricingRecommendation[];
};

export type PricingInsightsFilters = {
    courtIds?: number[];
    sportTypes?: string[];
};

export const GET_PRICING_INSIGHTS_QUERY_KEY = (
    locationId: string,
    filters?: PricingInsightsFilters,
) => ['pricing-insights', locationId, filters ?? {}];

export const useGetPricingInsights = (
    locationId: string,
    filters?: PricingInsightsFilters,
) => {
    const apiClient = useApiClient();

    return useQuery<PricingInsightsResult>({
        queryKey: GET_PRICING_INSIGHTS_QUERY_KEY(locationId, filters),
        queryFn: () => {
            const params = new URLSearchParams({ locationId });
            if (filters?.courtIds?.length) {
                params.set('courtIds', filters.courtIds.join(','));
            }
            if (filters?.sportTypes?.length) {
                params.set('sportTypes', filters.sportTypes.join(','));
            }
            return apiClient.fetchApi<PricingInsightsResult, {}>({
                method: 'GET',
                endpoint: `court-pricing/pricing-insights?${params.toString()}`,
            });
        },
        enabled: !!locationId,
    });
};
